import { pubSub } from '@utils/PubSub';
import { arraysEqual, deepClone } from '@utils/performance';
import { fetchStorefront, clean } from '@utils/GraphQLClient';
import { formatSecondaryProducts, formatID } from '@utils/format';
import { PRODUCT_QUERY, GALLERY_QUERY, ALIASES_QUERY, SECONDARY_PRODUCTS_QUERY, COMPONENT_FROM_SELECTIONS_QUERY, UPSELL_ITEM, UPGRADE_ITEM } from './Queries';

const headers = async (handle, query) => {
  return {
    variables: {
      handle,
    },
    query,
  };
};

export const actions = {
  async fetchProduct(handle, isSecondary = false) {
    try {
      const request = await headers(handle, PRODUCT_QUERY);
      const response = await fetchStorefront(request).then((response) => {
        const item = clean(response?.data?.product) ?? false;
        if (item && isSecondary) {
          this.secondaryItems.push(item);
        }
        return item;
      });
      return response;
    } catch (err) {
      return false;
    }
  },
  async fetchGallery(handle) {
    try {
      const request = await headers(handle, GALLERY_QUERY);
      const response = await fetchStorefront(request).then((response) => {
        const data = clean(response?.data?.product?.media);
        const gallery = data.edges.flatMap((img) => img.node);
        if (response?.data?.product?.image_gallery_infographic) {
          response?.data?.product?.image_gallery_infographic.metaobjects.nodes.forEach((metaobject) => {
            gallery.push(this.fetchImageGalleryInfographic(metaobject));
          });
        }
        if (response?.data?.product?.image_gallery_video) {
          response?.data?.product?.image_gallery_video.metaobjects.nodes.forEach((metaobject) => {
            gallery.push(this.fetchImageGalleryVideo(metaobject));
          });
        }
        return gallery;
      });
      return response;
    } catch (err) {
      return false;
    }
  },
  async fetchUpsellItem(payload = false) {
    this.upsell.loaded = false;
    payload = payload ? payload : this.upsell.items;
    if (payload.length <= 0) return;

    const items = [];
    for (const item of payload) {
      const handle = item.trim();
      const request = await headers(handle, UPSELL_ITEM);
      const response = await fetchStorefront(request).then((res) => {
        const data = clean(res?.data?.product);
        return data;
      });
      items.push(response);
    }
    this.upsell.items = items;
    this.upsell.loaded = true;
    return;
  },
  async fetchUpgradeItem(payload = false) {
    payload = payload ? payload : this?.item?.upgrade?.reference?.product.value;

    if (!payload) return;

    const request = {
      variables: {
        id: payload,
      },
      query: UPGRADE_ITEM,
    };

    const response = await fetchStorefront(request).then((res) => {
      const data = clean(res?.data?.product);
      return data;
    });

    this.upgrade = {
      text: this?.item?.upgrade?.reference?.text.value,
      product: response,
    };
  },
  fetchImageGalleryInfographic(data) {
    const metaobject = data.fields;
    const getField = (key) => metaobject.find((field) => field.key === key);

    const imageUrl = getField('image')?.object.image.url;
    const imageZoomUrl = getField('image')?.object.image.zoom;
    const infographicObj = {
      alt: `PDP2 Option[All] ${getField('gallery_position')?.value || 'LIFESTYLE'} METAOBJECT`,
      image: { url: imageUrl },
      zoom: { url: imageZoomUrl },
      infographic: true,
    };
    return infographicObj;
  },
  fetchImageGalleryVideo(data) {
    const metaobject = data.fields;
    const getField = (key) => metaobject.find((field) => field.key === key);

    const videoSources = getField('video')?.object.sources;

    const posterImage = getField('poster_image') ? getField('poster_image')?.object.image.url : getField('video')?.object.previewImage.url;
    const subtitleFile = getField('subtitle')?.object.url;
    const videoObj = {
      alt: `PDP2 Option[All] ${getField('gallery_position')?.value || 'MAIN'} METAOBJECT`,
      sources: videoSources,
      previewImage: { url: posterImage },
      subtitles: subtitleFile,
      controls: {
        audio: JSON.parse(getField('audio')?.value),
        autoplay: JSON.parse(getField('autoplay')?.value),
        controls: JSON.parse(getField('controls')?.value),
      },
    };
    return videoObj;
  },
  componentSelect(index, option) {
    const current = this.currentStep;
    this.steps[current].selected.options[index] = option;

    const variants = this.steps[current].item.variants;
    const options = this.steps[current].selected.options;
    const selections = options.filter((option) => option !== '');
    const allSelectionsMade = selections.length >= this.steps[current].totalOptions;
    const updateGallery = index === this.steps[current].galleryIndex ?? false;

    if (updateGallery) {
      const value = option.value === 'Default Title' ? 'Default' : option.value;
      this.steps[current].selected.gallery = value;
    }

    this.steps[current].selected.matches = variants.filter((variant) => {
      const { selectedOptions } = variant;
      const match = selectedOptions.find((option) => option.value === options[index].value);
      return match;
    });

    if (!allSelectionsMade) return;

    const match = variants.filter((variant) => {
      const clonedOptions = JSON.parse(JSON.stringify(selections));
      clonedOptions.forEach((option) => delete option.aliases);
      return arraysEqual(variant.selectedOptions, clonedOptions);
    });

    if (match.length <= 0) return;

    const params = new URLSearchParams(window.location.search);
    const id = parseInt(match[0]?.id.split('ProductVariant/')[1]);
    this.steps[current].selected.variant = match[0];
    this.steps[current].selected.available = this.steps[current].selected.variant?.quantityAvailable > 0;
    const key = `component${this.currentStep}`;
    const hasComponentParam = params.has(key) ?? false;
    if (window.location.pathname.includes('product')) {
      if (hasComponentParam) {
        params.set(key, id);
      } else {
        params.append(key, id);
      }

      const url = `${window.location.pathname}?${params}`;
      window.history.replaceState({}, '', url);
    }
  },
  select(index, option) {
    this.selected.options[index] = option;

    const variants = this.item.variants;
    const options = this.selected.options;
    const selections = options.filter((option) => option !== '');
    const allSelectionsMade = selections.length >= this.totalOptions;
    const updateGallery = index === this.galleryIndex ?? false;
    const isAutoBundle = this.isAutoBundle;

    if (updateGallery) {
      this.updateGallery(option);
      pubSub.publish('pdp:selection:update', option);
    }

    if (isAutoBundle) this.autoselectComponents(allSelectionsMade);

    this.selected.matches = [];

    selections.forEach((selection) => {
      const matches = variants.filter((variant) => {
        const { selectedOptions } = variant;
        const match = selectedOptions.find((option) => option.value === selection.value);
        return match;
      });
      this.selected.matches.push(matches);
    });

    this.selected.matches = this.selected.matches.flat();
    this.item.fromPrice.value = this.updateFromPrice(this.selected);
    if (!allSelectionsMade) return;

    const match = variants.filter((variant) => {
      const clonedOptions = JSON.parse(JSON.stringify(selections));
      clonedOptions.forEach((option) => delete option.aliases);
      return arraysEqual(variant.selectedOptions, clonedOptions);
    });

    if (match.length > 0) {
      this.selected.variant = match[0];
      this.variantSelectTracking(match[0].sku, match[0]);
    }

    this.selected.available = this.selected.variant?.quantityAvailable > 0;
    this.updateURL();
  },
  variantSelectTracking(sku) {
    if (sku) {
      window.dataLayer.push({
        event: 'variant_selected',
        selectedVariantSku: sku,
      });
    }
  },
  updateFromPrice(selected = {}) {
    /**
     * @var {Boolean, Object} variant is either an object representing a specific product variant, or it's false if no variant has been selected meaning only gallery option is selected.
     *
     * @var {Object} selectedMatch Checks for a variant. If a variant is selected,selectedMatch will equal that variant object. Otherwise, it defaults to the first matching variant object based on the current gallery selection.
     *
     * @var {Null, String} compareAtPrice will either be null if no variant compare-at price is set, or it will be the variant's compare-at price represented as a string.
     */
    const variant = selected.variant;
    const selectedMatch = variant ? variant : selected.matches[0];
    const compareAtPrice = selectedMatch.compareAtPrice?.amount;
    /**
     * After determining the variant, we update the item's from price.
     * If a compare-at price exists, it becomes the from price.
     * Otherwise, we use the product's metafield from price.
     * We have updated the GraphQL query to include the products from price metafield in each variant.
     * Storing the from price metafield in variants allows dynamic pricing for primary or secondary product selections.
     */
    return compareAtPrice ? parseFloat(compareAtPrice) * 100 : selectedMatch.product.fromPrice.value;
  },
  async autoselectComponents(allSelectionsMade) {
    if (!allSelectionsMade || this.components.length <= 0) return;

    const options = this.selected.options.reduce((arr, current) => {
      const hasAliases = this.item.aliases && this.item.aliases.size > 0;
      if (!hasAliases) {
        arr.push(current);
        return arr;
      }

      const matchingAlias = this.item.aliases.has(current.value);
      if (!matchingAlias) {
        arr.push(current);
        return arr;
      }

      const aliases = this.item.aliases.get(current.value);
      current.aliases = aliases;
      arr.push(current);
      return arr;
    }, []);

    const bundleSavings = [];
    const components = this.components.reduce((arr, current, index) => {
      const hasMultipleComponents = typeof current !== 'string';

      const clonedOptions = JSON.parse(JSON.stringify(options));
      const selections = clonedOptions.map((option) => {
        const hasAliases = option?.aliases ?? false;
        if (!hasAliases) return option;

        const alias = option.aliases[index];
        option.value = alias;
        delete option.aliases;
        return option;
      });

      let handle = current;

      if (hasMultipleComponents) {
        handle = current[0];
        const secondaryItems = this.secondaryItems;
        const secondaryIndex = secondaryItems.findIndex((item) => {
          const variants = item.variants;
          const matchingVariant = variants.findIndex((variant) => {
            const options = deepClone(variant.selectedOptions).map((option) => {
              delete option.aliases;
              return option;
            });

            return arraysEqual(options, selections);
          });

          return matchingVariant >= 0;
        });

        if (secondaryIndex >= 0) {
          handle = current[secondaryIndex + 1];
        }
      }

      const variables = {
        handle: handle,
        options: selections,
      };

      arr.push(variables);
      return arr;
    }, []);

    const selected = [];
    for (const [index, component] of components.entries()) {
      const request = {
        variables: component,
        query: COMPONENT_FROM_SELECTIONS_QUERY,
      };
      try {
        const response = await fetchStorefront(request).then((res) => {
          const item = clean(res?.data?.product) ?? false;
          if (item?.promotedOptionGroups) {
            item.promotedOptionGroups = this.formatPOGs(item);
          }
          return item;
        });

        bundleSavings.push('');
        selected.push(response);
      } catch (err) {
        throw new Error(`${component} Fetch Failed. ${err}`);
      }
    }

    this.selected.components = selected;
    this.handleComponentAvailability();
  },
  handleComponentAvailability() {
    //update prices for selected components
    this.updateAutoPrices();

    const { components } = this.selected;
    // Checks to see if any selected component of Autobundle are unavailable
    const unavailableItems = components.filter((component) => {
      const { quantityAvailable } = component.variantBySelectedOptions;
      return quantityAvailable <= 0;
    });

    const soldOut = unavailableItems.length === components.length;

    this.selected.available = unavailableItems.length === 0;
    this.selected.bundleUnavailable = soldOut;

    if (!soldOut) return this.scrollToReview();
  },
  scrollToReview() {
    const hasAlreadyScrolled = this.assistedScroll ?? false;

    if (hasAlreadyScrolled) return;
    this.assistedScroll = true;

    setTimeout(() => {
      const reviewArea = document.querySelector('.BundleReview');
      reviewArea.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }, 400);
  },
  updateGallery(option) {
    const value = option.value === 'Default Title' ? 'Default' : option.value;
    this.selected.gallery = value;
  },
  updateURL() {
    const isBundle = this.isSteppedBundle;
    const params = new URLSearchParams(window.location.search);

    if (isBundle) {
      const hasStepParam = params.has('step') ?? false;

      if (hasStepParam) {
        params.set('step', this.currentStep);
      } else {
        params.append('step', this.currentStep);
      }

      const url = `${window.location.pathname}?${params}`;
      window.history.replaceState({}, '', url);
    }

    const variant = this.selected.variant;
    if (!variant) return;

    const id = parseInt(variant.id.split('ProductVariant/')[1]);
    const hasVariantParam = params.has('variant') ?? false;
    const hasGalleryParam = params.has('galopt') ?? false;

    if (hasVariantParam) {
      params.set('variant', id);
    } else {
      params.append('variant', id);
    }

    if (hasGalleryParam && !isBundle) params.delete('galopt');

    const url = `${window.location.pathname}?${params}`;
    return window.history.replaceState({}, '', url);
  },
  async setInititalState(props) {
    if (props) {
      let options = 1;
      let id = props.variant || props.selectedVariant.id;
      const item = this.item;
      const tags = item.tags ?? false;
      const variants = item.variants ?? false;
      const isBundle = this.isBundle ?? false;
      const url = new URL(window.location);
      if (item?.promotedOptionGroups) {
        const groups = this.formatPOGs(item);
        this.item.promotedOptionGroups = groups;
      }

      this.settings = props.settings;
      const galleryTag = tags.filter((tag) => tag.includes('Gallery Option:'));
      const galleryOption = galleryTag[0].split(':')[1].trim();
      const optionName = galleryOption.includes('Default') ? 'Title' : galleryOption;

      this.galleryIndex = item.options.findIndex((option) => option.name === optionName);
      this.totalOptions = variants[0].selectedOptions.map((option) => option.name).length;
      this.updateHiddenOptions(tags);

      if (isBundle) {
        this.components = tags.filter((tag) => /Component \d:/.test(tag)).map((tag) => tag.split(':')[1].trim());

        if (this.secondaryItems.length > 0) {
          const componentTags = this.secondaryItems
            .flatMap((item) => item.tags)
            .filter((tag) => /Component \d:/.test(tag))
            .map((tag) => tag.split(':')[1].trim());

          componentTags.forEach((component, index) => {
            const handle = this.components[index];
            if (component !== handle) {
              this.components[index] = [handle, component];
            }
          });
        }
      }

      do {
        options += 1;
        this.selected.options.push('');
        //creates placeholders for variant selections
      } while (options <= this.totalOptions);

      const index = this.galleryIndex;

      /**
       * Extract the variant parameter from the URL and update the ID if necessary.
       * @param {URL} url - The URL object to extract the search parameter from.
       * @param {number} id - The current variant ID.
       */
      const paramVariant = parseInt(url.searchParams.get('variant')) || false;
      if (paramVariant && paramVariant !== id) id = paramVariant;

      /**
       * Find the selected variant matching the current ID or fallback to the first variant.
       * @type {Object} selectedVariant - The selected variant object.
       */
      const selectedVariant = variants.find((variant) => formatID(variant.id) === id) || variants[0];

      /**
       * Determine the featured gallery option based on the selected variant.
       * @type {string} featuredGalleryOption - The value of the selected gallery option.
       */
      const featuredGalleryOption = selectedVariant.selectedOptions[index].value;

      /**
       * Filter the variants to find those that match the gallery option and have available quantity.
       * @type {Array<Object>} possibleAvailableVariants - Variants matching the gallery option with stock.
       */
      const possibleVariants = variants.filter((variant) => variant.selectedOptions[index].value === featuredGalleryOption);

      /**
       * Select the options for a given variant.
       * @param {Array<Object>} variantOptions - The options to select.
       */
      const selectVariantOptions = (options) => {
        if (isBundle) {
          const index = this.galleryIndex;
          const value = options[index];
          this.select(index, value);
        } else {
          options.forEach((option, index) => this.select(index, option));
        }
      };

      /**
       * Handle variant selection based on availability:
       * 1. If the selected variant has stock, use it.
       * 2. Otherwise, use the first available variant matching the gallery option to limit out of stock states
       * 3. Fallback to the selected variant if no matching gallery variants are available.
       */

      let optionsToSelect = [selectedVariant];

      if (selectedVariant.quantityAvailable <= 0) {
        const possibleAvailableVariants = possibleVariants.filter((variant) => variant.quantityAvailable > 0);
        const possibleMatches = possibleAvailableVariants.length > 0 ? possibleAvailableVariants : optionsToSelect;

        if (possibleMatches.length > 0) {
          optionsToSelect = possibleMatches;
        } else {
          optionsToSelect = variants;
        }
      }

      selectVariantOptions(optionsToSelect[0].selectedOptions);

      // Swatch Sections
      const evergreenSwatchTag = tags.filter((tag) => tag.includes('Evergreen:'));

      if (evergreenSwatchTag.length > 0) {
        this.evergreenSwatchCount = parseInt(evergreenSwatchTag[0].split(':')[1].trim());
      }

      await this.fetchUpsellItem();
      await this.fetchUpgradeItem();

      // Modals
      const modalKeys = tags.filter((tag) => tag.includes('PDP Modal')).map((tag) => tag.split(':')[1].trim());

      this.modals = modalKeys;

      //Remove loading CSS class on main product
      document.querySelector('.js-main-product')?.classList.remove('Product--Loading');
    }
    this.fetchTheLookProducts();
  },
  fetchTheLookProducts() {
    /**
     * Extracts "stl-product" and "stl-product-sku" parameters from the URL and returns an array of objects.
     *
     * This function processes URL parameters to identify product handles and SKUs,
     * organizing them into an array where each product has a `handle` and `sku`.
     *
     * @returns {Array<{ handle: string, sku: string }>}
     */
    const params = new URLSearchParams(window.location.search);
    const items = Array.from(params.entries()).reduce((items, [key, value]) => {
      const match = key.match(/^stl-product(?:-sku)?-(\d+)$/);

      if (match) {
        const index = parseInt(match[1]) - 1;
        items[index] = items[index] || { handle: '', variant: '' };
        key.includes('sku') ? (items[index].variant = value) : (items[index].handle = value);
      }

      return items;
    }, []);

    this.shopTheLook = items;
  },
  async fetchOptionAliases() {
    const handle = this.item.handle ?? false;
    const request = await headers(handle, ALIASES_QUERY);
    const response = await fetchStorefront(request).then((response) => {
      const aliases = response?.data?.product?.metafield?.references?.nodes ?? false;
      if (!aliases) return;

      this.item.aliases = aliases.reduce((obj, current) => {
        const key = current.option.value;
        const values = Array.from(JSON.parse(current.aliases.value));
        obj[key] = values;
        return obj;
      }, {});

      this.item.aliases = new Map(Object.entries(this.item.aliases));

      return;
    });
  },
  async fetchSecondaryItemsForBundle(handle) {
    try {
      this.secondaryItems = [];
      const request = await headers(handle, SECONDARY_PRODUCTS_QUERY);
      const response = await fetchStorefront(request).then((response) => formatSecondaryProducts(response));

      const handles = response?.handles ?? [];
      if (!response || handles.length <= 0) return;

      for (const [index, handle] of handles.entries()) {
        const item = await this.fetchProduct(handle);
        const media = await this.fetchGallery(handle);
        const label = response?.labels && response?.labels[index] ? response?.labels[index] : '';

        let limit = response?.limits && response?.limits[index] ? response?.limits[index] : false;

        const { tags, options, variants } = item;
        const colorIndex = options.findIndex((option) => option.name === 'Color');
        const mainColorIndex = this.item.options.findIndex((option) => option.name === 'Color');
        if (colorIndex <= -1 || mainColorIndex <= -1) return;

        const colors = options[colorIndex].values;
        const swatches = limit ? colors.slice(0, limit) : colors;
        limit = swatches.length;

        const filteredSaleMedia = media.filter((asset) => {
          if (asset.alt !== '' && !asset.alt.includes('FEED')) {
            const alt = asset.alt.split('[')[1].trim().split(']')[0].trim();
            return swatches.includes(alt);
          }
        });

        const filteredMedia = this.gallery.filter((asset) => {
          if ((asset.alt !== '' && !asset.alt.includes('FEED')) || asset.sources) {
            const alt = asset.alt.split('[')[1].trim().split(']')[0].trim();
            return !swatches.includes(alt);
          }
        });

        item.limit = limit;
        item.label = label;
        this.updateHiddenOptions(tags);
        this.item.variants = [...this.item.variants, ...variants];
        this.gallery = [...filteredMedia, ...filteredSaleMedia];
        this.secondaryItems.push(item);
      }
      return;
    } catch (err) {
      throw new Error(`Secondary Items fetch failed: ${err}`);
    }
  },
  async setSecondaryProducts(props) {
    /*
     * "Secondary Products" refers to options associated with separate products
     * that have the same exact options. For example, exposing the swatches of
     * the Last Call core set on the full price core set PDP.
     *
     * Note: we use a nested JSON.parse when setting the limit bc ACF converts
     * numbers to strings, and then the Liquid JSON filter escapes the string's
     * quotes, requiring a double parse to get the value into the desired array
     * format.
     */
    const secondaryProducts = props?.secondaryProducts ?? [];

    if (secondaryProducts.length <= 0 || secondaryProducts === 'null') {
      return;
    }

    for (const [index, handle] of secondaryProducts.entries()) {
      try {
        const item = await this.fetchProduct(handle);
        const media = await this.fetchGallery(handle);
        item.label = props.secondaryProductsLabels[index];
        const limit = JSON.parse(JSON.parse(props.secondaryProductsLimits))[index];
        item.limit = limit;

        const { options, variants } = item;
        const colorIndex = options.findIndex((option) => option.name === 'Color');
        const swatches = options[colorIndex].values.slice(0, limit);
        const filteredSaleMedia = media.filter((asset) => {
          if (asset.alt !== '' && !asset.alt.includes('FEED')) {
            const alt = asset.alt.split('[')[1].trim().split(']')[0].trim();
            return swatches.includes(alt);
          }
        });

        const filteredMedia = this.gallery.filter((asset) => {
          if (asset.alt !== '' && !asset.alt.includes('FEED')) {
            const alt = asset.alt.split('[')[1].trim().split(']')[0].trim();
            return !swatches.includes(alt);
          }
        });
        const { tags } = item;
        this.updateHiddenOptions(tags);

        if (item?.promotedOptionGroups) {
          item.promotedOptionGroups = this.formatPOGs(item);
        }

        this.secondaryItems.push(item);
        this.item.variants = [...this.item.variants, ...variants];
        this.gallery = [...filteredMedia, ...filteredSaleMedia];
      } catch (err) {
        return err;
      }
    }
  },
  async generateSteps(props) {
    const handle = props?.handle?.substring(1);
    const item = await this.fetchProduct(handle);
    const { tags } = item;
    this.updateHiddenOptions(tags);
    const handles = tags.filter((tag) => /Component \d:/.test(tag));
    const hasComponents = handles.length > 0;
    if (!hasComponents) return;

    this.item = item;

    const builder = {
      step: 0,
      type: 'start',
      handle: handle,
      item: item,
      gallery: {},
      galleryIndex: 0,
      totalOptions: 0,
      mounted: false,
      fetched: false,
      evergreenSwatchCount: 0,
      selected: {
        quantity: 1,
        matches: [],
        options: [],
        gallery: false,
        variant: false,
      },
    };

    const bundleSavings = [];
    const components = handles.reduce((arr, current, index) => {
      const obj = {
        step: index + 1,
        type: 'product',
        handle: current.split(':')[1].trim(),
        item: false,
        gallery: {},
        galleryIndex: 0,
        totalOptions: 0,
        mounted: false,
        fetched: false,
        evergreenSwatchCount: 0,
        selected: {
          quantity: 1,
          matches: [],
          options: [],
          gallery: false,
          variant: false,
        },
      };
      bundleSavings.push('');
      arr.push(obj);
      return arr;
    }, []);

    const review = {
      step: components.length + 1,
      type: 'review',
      handle: 'review',
      item: item,
      gallery: {},
      galleryIndex: 0,
      totalOptions: 0,
      mounted: false,
      fetched: true,
      evergreenSwatchCount: 0,
      selected: {
        quantity: 1,
        matches: [],
        options: [],
        components: [],
        gallery: false,
        variant: false,
      },
    };

    let index = 0;

    do {
      review.selected.components.push('');
      index += 1;
    } while (index < components.length);

    this.updateURL();

    return [builder, ...components, review];
  },
  async step(operation) {
    const isIndex = typeof operation === 'number';
    const current = this.currentStep;
    const increment = operation === '+';
    const selections = this.steps[current].selected.options.filter((option) => option !== '');
    const allSelectionsMade = selections.length >= this.steps[current].totalOptions;
    const pushToReview = allSelectionsMade && current > 0;

    if (isIndex) {
      this.instance += 1;
      this.steps[current].mounted = false;
      this.currentStep = operation;
      return;
    }

    if (pushToReview) {
      const reviewStep = this.steps[this.steps.length - 1];
      const currentStep = this.steps[current];
      const { selected, item } = currentStep;
      let handle = item.handle;

      if (selected.variant.product.id !== item.id) {
        const secondaryItems = currentStep?.secondaryItems ?? [];
        const matchingItem = secondaryItems.find((item) => item.id === selected.variant.product.id);

        handle = matchingItem.handle;
      }

      const request = {
        variables: {
          handle: handle,
          options: selected.options,
        },
        query: COMPONENT_FROM_SELECTIONS_QUERY,
      };

      const response = await fetchStorefront(request).then((res) => {
        const item = clean(res?.data?.product) ?? false;
        if (item?.promotedOptionGroups) {
          item.promotedOptionGroups = this.formatPOGs(item);
        }
        return item;
      });

      reviewStep.selected.components[current - 1] = response;
      reviewStep.selected.gallery = 'Bundle';
      reviewStep.gallery = reviewStep.selected.components.reduce((arr, current) => {
        if (current === '') return arr;

        const image = current.variantBySelectedOptions.image;
        const alt = `PDP2 Option[Bundle] MAIN`;
        const zoom = current.variantBySelectedOptions.zoom;

        arr.push({ alt, image, zoom });
        return arr;
      }, []);
      this.steps[this.steps.length - 1] = reviewStep;
      this.updateSteppedPrices();
    }

    this.instance += 1;
    this.steps[current].mounted = false;

    if (increment) {
      this.currentStep += 1;
    } else {
      this.currentStep -= 1;
    }
  },
  async updateAutoPrices(variant, tags = false) {
    const autoTags = this.item.tags ?? tags;
    if (!autoTags) return;
    // Bundle Discount
    const bundleDiscount = autoTags.filter((tag) => tag.includes('Discount Amount')).map((tag) => parseInt(tag.split(':')[1]))[0] / 100;
    const noDiscount = autoTags.some((tag) => tag.includes('Product Bundle No Discount'));
    const noSwdDiscount = () => autoTags.includes('Discount Codes: Deny');

    // Promo Object SWD discount
    const promo = await window.Brooklinen?.promo?.discounts;
    const swdEnabled = promo?.swd?.enabled;
    const swdAmount = promo?.swd?.amount / 100;
    const promoTags = promo?.swd?.tags?.split(',');
    let match = autoTags?.filter((value) => promoTags.includes(value));
    let swdPrice = 0;
    let discountedPrices = [];

    this.selected.components.map((product, index) => {
      const newSelection = variant?.product?.id === product.variantBySelectedOptions.product?.id;
      const newPrice = variant?.price?.amount * 100;
      const data = {};
      const price = newSelection ? newPrice : product.variantBySelectedOptions?.price?.amount * 100;
      const comparePrice = product.variantBySelectedOptions?.compareAtPrice?.amount * 100;

      // If there is a compare price use this price as the default from pricing
      const originalPrice = comparePrice ? comparePrice : price;
      data.price = parseFloat(originalPrice);
      // Discounting is applied category first, bundle second. Need to use the discounted category price if it exists
      const priceManualDiscounts = comparePrice ? price : originalPrice;
      const discountPercentage = 1 - bundleDiscount;
      let bundlePrice = priceManualDiscounts * discountPercentage;
      let bundleSavings = (originalPrice * bundleDiscount) / 100;

      //  Need Bundle component level savings to send to analytics
      this.bundleSavings[index] = parseFloat(bundleSavings);
      const qualifiesSwd = swdEnabled && match;

      // Bundle & SWD discount
      if (qualifiesSwd && !noSwdDiscount()) {
        const discountAmount = 1 - swdAmount;
        swdPrice = (bundlePrice * discountAmount).toFixed(2);
      }

      const salePrice = swdPrice > 0 ? swdPrice : bundlePrice;
      const subtotal = noDiscount ? price : salePrice;
      data.discountedPrice = parseFloat(subtotal / 100).toFixed(2);
      discountedPrices.push(data);
      return data;
    });
  },
  async updateSteppedPrices() {
    const current = this.currentStep;
    const reviewStep = this.steps[this.steps.length - 1];
    const currentComponent = reviewStep?.selected?.components[current - 1];
    const shellComponent = this.steps[0];
    const shellTags = shellComponent?.item?.tags ?? this.item.tags;

    // Default Price, when compare price exists item is part of category promo
    const steppedPrice = currentComponent?.variantBySelectedOptions?.price?.amount;
    const price = steppedPrice * 100;
    const comparePrice = currentComponent?.variantBySelectedOptions?.compareAtPrice?.amount * 100;
    const compareSavings = comparePrice - price;
    // If compare exists it should be the new price
    const originalPrice = comparePrice ? comparePrice : price;
    // Bundle Discount
    const bundleDiscount = shellTags.filter((tag) => tag.includes('Discount Amount')).map((tag) => parseInt(tag.split(':')[1]))[0] / 100;
    const noDiscount = shellTags.some((tag) => tag.includes('Product Bundle No Discount'));
    const noSwdDiscount = () => shellTags.includes('Discount Codes: Deny');

    const discountPercentage = 1 - bundleDiscount;
    // Discounting is applied category first, bundle second. Need to use the discounted category price if it exists
    const priceManualDiscounts = comparePrice ? price : originalPrice;
    let bundlePrice = priceManualDiscounts * discountPercentage;
    let bundleSavings = price * bundleDiscount;
    let bundleCategorySavings = compareSavings + bundleSavings;
    // Take into account compare price savings when there is category promo sale
    let componentSavings = comparePrice ? parseFloat(bundleCategorySavings / 100).toFixed(2) : parseFloat(bundleSavings / 100).toFixed(2);

    // Need Bundle component level savings to send to analytics
    this.bundleSavings[current - 1] = parseFloat(componentSavings);

    // Promo Object SWD discount
    const promo = await window.Brooklinen.promo.discounts;
    const swdEnabled = promo.swd.enabled;
    const swdAmount = promo.swd.amount / 100;
    const promoTags = promo.swd.tags.split(',');
    let match = shellTags.filter((value) => promoTags.includes(value));
    let swdPrice = 0;
    const qualifiesSwd = swdEnabled && match;

    // Bundle & SWD
    if (qualifiesSwd && !noSwdDiscount()) {
      const discountAmount = 1 - swdAmount;
      swdPrice = bundlePrice * discountAmount;
    }

    const salePrice = swdPrice > 0 ? swdPrice : bundlePrice;
    const subtotal = noDiscount ? originalPrice : salePrice;
  },
  mountPromo() {
    pubSub.unsubscribe('dy:promo:init', this.mountPromo);
    pubSub.publish('dy:promo:ready');
  },
  mountFallback() {
    pubSub.unsubscribe('dy:promo:init', this.mountFallback);
    pubSub.publish('dy:promo:ready');
  },
  setPromo() {
    this.promoReady = true;
  },
  mountPromoListener() {
    pubSub.subscribe('dy:promo:init', this.mountPromo, this.mountFallback);
    pubSub.subscribe('dy:promo:ready', this.setPromo);
  },
  updateHiddenOptions(tags) {
    if (tags.length <= 0) {
      this.hiddenOptions = tags;
      return;
    }

    const hiddenOptions = tags.filter((tag) => tag.includes('Hide Option Value:')).map((tag) => tag.split(':')[1].trim());

    if (this.isSteppedBundle) {
      this.hiddenOptions = hiddenOptions;
    } else {
      this.hiddenOptions = [...this.hiddenOptions, ...hiddenOptions];
    }
  },
  formatPOGs(item) {
    const promotedOptionGroups = item.promotedOptionGroups.references?.nodes ?? [];
    promotedOptionGroups.forEach((group) => {
      const values = JSON.parse(group.values.value);
      group.values = values;
    });
    return promotedOptionGroups;
  },
};
